import { Alert, Button, Group, Loader, Stack, Text } from '@mantine/core'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { IconExclamationMark } from '@tabler/icons-react'
import { useState } from 'react'

interface StripePaymentFormProps {
  onPaymentSuccess: () => void
  onBack: () => void
  isUpdating: boolean
  clientSecret?: string
}

export function StripePaymentForm({ onPaymentSuccess, onBack, isUpdating, clientSecret }: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Helper function to determine if we have a SetupIntent or PaymentIntent
  const isSetupIntent = (secret: string | undefined): boolean => {
    return secret?.startsWith('seti_') ?? false
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      let error: any

      if (isSetupIntent(clientSecret)) {
        // Use confirmSetup for SetupIntents (payment method setup without charging)
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/dashboard/billing`,
          },
          redirect: 'if_required',
        })
        error = result.error
      } else {
        // Use confirmPayment for PaymentIntents (actual purchases)
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/dashboard/billing`,
          },
          redirect: 'if_required',
        })
        error = result.error
      }

      if (error) {
        setErrorMessage(error.message || 'An unexpected error occurred.')
      } else {
        // Success - either payment or setup completed
        onPaymentSuccess()
      }
    } catch (_err) {
      setErrorMessage('An unexpected error occurred.')
    } finally {
      setIsProcessing(false)
    }
  }

  const isFormDisabled = !stripe || !elements || isUpdating || isProcessing

  return (
    <Stack gap="md" p="lg" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '8px' }}>
      <Text fw={600} size="lg">
        Payment Information
      </Text>

      <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          {/* Stripe Payment Element */}
          <div style={{ minHeight: '200px' }}>
            <PaymentElement
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card', 'us_bank_account'],
              }}
            />
          </div>

          {/* Error Display */}
          {errorMessage && (
            <Alert variant="light" color="red" title="Payment Failed" icon={<IconExclamationMark />}>
              {errorMessage}
            </Alert>
          )}

          {/* Action Buttons */}
          <Group justify="space-between" mt="md">
            <Button variant="default" onClick={onBack} disabled={isFormDisabled} size="md">
              Back
            </Button>
            <Button type="submit" disabled={isFormDisabled} size="md" color="indigo">
              {isProcessing || isUpdating ? (
                <Group gap="xs">
                  <Loader size="xs" />
                  <span>{isProcessing ? 'Processing...' : 'Completing setup...'}</span>
                </Group>
              ) : isSetupIntent(clientSecret) ? (
                'Save Payment Method'
              ) : (
                'Complete Purchase'
              )}
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  )
}
